import React, { useState } from "react";
import {
  formatDisplayDate,
  parseYYYYMMDD,
} from "../../../../../classes/utils/DateUtils";
import { useUser } from "../../../../../classes/contexts/UserContext";
import { useAppContext } from "../../../../../classes/contexts/AppContext";
import { DataPermissions } from "../../../../../classes/enums/PermissionsEnum";
import { ButtonSize } from "../../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../../classes/enums/IconsEnum";
import IconButton from "../../../../../components/iconButton/IconButton";
import DownArrowIcon from "../../../../../components/icons/DownArrowIcon";
import UpArrowIcon from "../../../../../components/icons/UpArrowIcon";
import { MedicalImageThumbnail } from "../../../../../components/medicalImageThumbnail/MedicalImageThumbnail";
import MovablePopup from "../../../../../components/popup/MovablePopup";
import { useAuth } from "../../../../../hooks/useAuth";
import { useRouteState } from "../../../../../hooks/useRouteState";
import { getInstanceThumbnail } from "../../../../../services/InstancesService";
import DeleteInstanceModal from "./DeleteInstanceModal";
import "./SeriesItem.css";

const SeriesItem = ({ seriesData, instances, isExpandedByDefault = false, onInstanceDeleted }) => {
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const canDelete = checkPermissions(DataPermissions.DELETE_INSTANCE);
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const [openWindows, setOpenWindows] = useState([]);
  const [imageSources, setImageSources] = useState({});
  const { params } = useRouteState();
  const { dateSettings } = useUser();

  // Load thumbnails for all instances when component mounts
  React.useEffect(() => {
    const loadThumbnails = async () => {
      const newImageSources = {};
      for (const instance of instances) {
        try {
          const response = await getInstanceThumbnail(
            params.patientId,
            instance._id
          );
          newImageSources[instance._id] = URL.createObjectURL(response.data);
        } catch (error) {}
      }
      setImageSources(newImageSources);
    };

    if (instances && instances.length > 0) {
      loadThumbnails();
    }
  }, [instances, params.patientId]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleImageClick = instance => {
    const imageSrc = imageSources[instance._id];
    if (!imageSrc) return;

    const formattedDate = formatDisplayDate(instance.creation_date, {
      locale: dateSettings?.locale,
      format: dateSettings?.format,
    });

    const newWindow = {
      id: Date.now(),
      image: {
        url: imageSrc,
        title: instance.file_name,
        date: formattedDate || "No date",
      },
    };
    setOpenWindows(prevWindows => [...prevWindows, newWindow]);
  };

  const handleCloseWindow = id => {
    setOpenWindows(prevWindows =>
      prevWindows.filter(window => window.id !== id)
    );
  };

  // Get modality from series or fallback to first instance
  const getModality = React.useCallback(() => {
    // Try series modality first
    if (seriesData?.modality) {
      return seriesData.modality;
    }

    // Fallback to first instance modality
    if (instances && instances.length > 0 && instances[0].modality) {
      return instances[0].modality;
    }

    return "-";
  }, [seriesData, instances]);

  // Get the earliest date from instances for display
  const getEarliestDate = () => {
    if (!instances || instances.length === 0) return "-";
    const dates = instances
      .map(instance => parseYYYYMMDD(instance.creation_date))
      .filter(Boolean);
    if (dates.length === 0) return "-";
    const earliestDate = new Date(Math.min(...dates));
    return (
      formatDisplayDate(earliestDate, {
        locale: dateSettings?.locale,
        format: dateSettings?.format,
      }) || "-"
    );
  };

  // Debug logging
  React.useEffect(() => {
    if (instances && instances.length > 0) {
    }
  }, [seriesData, instances, getModality]);

  return (
    <div className="instance-item">
      <div className="instance-title" onClick={toggleExpand}>
        <div className="instance-title-left">
          <div>{getModality()}</div>
          <div>{getEarliestDate()}</div>
        </div>
        <div className="instance-title-right">
          <div className="App-spinesim-study-arrow">
            <div className="App-spinesim-study-arrow-icon">
              {isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`instance-details ${
          isExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="instance-details-content">
          <div className="instance-thumbnail-container">
            {instances.map(instance => (
              <div key={instance._id} className="instance-thumbnail-item" style={{ position: "relative" }}>
                <MedicalImageThumbnail
                  instance={{
                    ...instance,
                    url: imageSources[instance._id],
                    date: instance.creation_date,
                  }}
                  isSelected={false}
                  onClick={() => handleImageClick(instance)}
                />
                {canDelete && (
                  <div
                    className="thumbnail-delete-btn"
                    style={{ position: "absolute", top: 4, right: 4, zIndex: 2 }}
                  >
                    <IconButton
                      icon={IconsEnum.DELETE}
                      size={ButtonSize.SMALL}
                      noBgColor
                      title="Delete Image"
                      onClick={e => {
                        e.stopPropagation();
                        showPopup(
                          <DeleteInstanceModal
                            instance={instance}
                            onInstanceDeleted={onInstanceDeleted}
                          />
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {openWindows.map((window, index) => (
        <MovablePopup
          key={window.id}
          index={index}
          title={window.image.title}
          subtitle={window.image.date}
          onClose={() => handleCloseWindow(window.id)}
          initialPos={{ x: 10 + index * 300, y: 25 }}
        >
          <img
            src={window.image.url}
            alt={window.image.title}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </MovablePopup>
      ))}
    </div>
  );
};

export default SeriesItem;
